<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import ContactAPI from 'dashboard/api/contacts';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  boardId: { type: [Number, String], required: true },
  stageId: { type: [Number, String], default: null },
});

const emit = defineEmits(['added']);

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const searchQuery = ref('');
const results = ref([]);
const isSearching = ref(false);
const selectedContact = ref(null);
const isAdding = ref(false);
let debounceTimer = null;

const canConfirm = computed(() => !!selectedContact.value && !isAdding.value);

const runSearch = async () => {
  if (!searchQuery.value || searchQuery.value.length < 2) {
    results.value = [];
    return;
  }
  isSearching.value = true;
  try {
    const response = await ContactAPI.search(searchQuery.value, 1);
    results.value = response.data.payload || [];
  } catch (error) {
    results.value = [];
  } finally {
    isSearching.value = false;
  }
};

const onSearchInput = () => {
  clearTimeout(debounceTimer);
  selectedContact.value = null;
  debounceTimer = setTimeout(runSearch, 350);
};

const onSelectContact = contact => {
  selectedContact.value = contact;
};

const reset = () => {
  searchQuery.value = '';
  results.value = [];
  selectedContact.value = null;
};

const open = () => {
  reset();
  dialogRef.value?.open();
};

const close = () => {
  dialogRef.value?.close();
};

const onConfirm = async () => {
  if (!selectedContact.value) return;
  isAdding.value = true;
  try {
    await store.dispatch('kanban/addItem', {
      boardId: props.boardId,
      contact_id: selectedContact.value.id,
      kanban_stage_id: props.stageId,
    });
    useAlert(t('KANBAN.ADD_CARD_MODAL.SUCCESS'));
    emit('added');
    close();
  } catch (error) {
    useAlert(t('KANBAN.ADD_CARD_MODAL.ERROR'));
  } finally {
    isAdding.value = false;
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('KANBAN.ADD_CARD_MODAL.TITLE')"
    :confirm-button-label="t('KANBAN.ADD_CARD_MODAL.ADD_BUTTON')"
    :cancel-button-label="t('KANBAN.ADD_CARD_MODAL.CANCEL_BUTTON')"
    :disable-confirm-button="!canConfirm"
    :is-loading="isAdding"
    @confirm="onConfirm"
  >
    <div class="flex flex-col gap-3">
      <Input
        v-model="searchQuery"
        :placeholder="t('KANBAN.ADD_CARD_MODAL.SEARCH_PLACEHOLDER')"
        @update:model-value="onSearchInput"
      />
      <div class="flex flex-col max-h-64 gap-1 overflow-auto">
        <div v-if="isSearching" class="flex justify-center p-3">
          <Spinner :size="20" />
        </div>
        <p
          v-else-if="searchQuery.length >= 2 && !results.length"
          class="p-3 text-sm text-center text-n-slate-11"
        >
          {{ t('KANBAN.ADD_CARD_MODAL.NO_RESULTS') }}
        </p>
        <button
          v-for="contact in results"
          :key="contact.id"
          type="button"
          class="flex items-center gap-2 p-2 text-left rounded-lg hover:bg-n-alpha-1"
          :class="{
            'bg-n-alpha-2': selectedContact && selectedContact.id === contact.id,
          }"
          @click="onSelectContact(contact)"
        >
          <Avatar
            :src="contact.thumbnail"
            :name="contact.name"
            :size="28"
            hide-offline-status
          />
          <div class="flex flex-col min-w-0">
            <span class="text-sm font-medium truncate text-n-slate-12">
              {{ contact.name }}
            </span>
            <span class="text-xs truncate text-n-slate-11">
              {{ contact.email || contact.phone_number }}
            </span>
          </div>
        </button>
      </div>
    </div>
  </Dialog>
</template>
